import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faPlus, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import webTargetService from '../services/webTargetService';
import targetService from '../services/targetService';

const WebTestingListPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [webTargets, setWebTargets] = useState([]);
  const [orphanTargets, setOrphanTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [createError, setCreateError] = useState('');
  const [recoveringId, setRecoveringId] = useState(null);

  const cardBg = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white';
  const surfaceBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-500';

  const fetchData = async () => {
    try {
      const [wtData, allTargets] = await Promise.all([
        webTargetService.getAll(projectId),
        targetService.getTargetsByProject(projectId)
      ]);
      const wts = Array.isArray(wtData) ? wtData : [];
      setWebTargets(wts);

      // Find webapplication targets with no corresponding WebTarget
      const linkedTargetIds = new Set(wts.map(wt => wt.target?._id || wt.target).filter(Boolean).map(String));
      const orphans = allTargets.filter(t =>
        t.type === 'webapplication' && !linkedTargetIds.has(String(t._id))
      );
      setOrphanTargets(orphans);
    } catch (err) {
      console.error('Error loading web targets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const handleCreate = async () => {
    if (!newUrl.trim()) return;
    setCreateError('');
    let url = newUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    try {
      await webTargetService.create({ baseUrl: url, projectId, webType: 'webapplication' });
      setNewUrl('');
      setShowForm(false);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to create web target';
      setCreateError(msg);
    }
  };

  const handleRecoverOrphan = async (target) => {
    setRecoveringId(target._id);
    try {
      await webTargetService.create({
        baseUrl: target.value,
        projectId,
        webType: 'webapplication',
        existingTargetId: target._id
      });
      await fetchData();
    } catch (err) {
      // If create fails because the target already exists, try creating WebTarget only
      console.error('Error recovering orphan target:', err);
    } finally {
      setRecoveringId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Web Application Testing</h1>
          <p className={`text-sm mt-1 ${textSecondary}`}>
            Configure and test web applications with proxy, spider, intruder, and more.
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setCreateError(''); }}
          className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Web Target
        </button>
      </div>

      {showForm && (
        <div className={`mb-6 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <h4 className={`text-sm font-medium mb-3 ${textPrimary}`}>New Web Target</h4>
          {createError && (
            <div className={`mb-3 px-3 py-2 rounded text-sm ${darkMode ? 'bg-red-900/30 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {createError}
            </div>
          )}
          <div className="flex gap-3">
            <input
              type="text"
              value={newUrl}
              onChange={e => { setNewUrl(e.target.value); setCreateError(''); }}
              placeholder="example.com or https://example.com"
              className={`flex-1 px-3 py-2 border rounded-md text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'border-gray-300'}`}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
            <button
              onClick={handleCreate}
              className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 text-sm"
            >
              Create
            </button>
            <button
              onClick={() => { setShowForm(false); setNewUrl(''); setCreateError(''); }}
              className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Orphan webapplication targets without WebTarget */}
      {orphanTargets.length > 0 && (
        <div className={`mb-6 p-4 rounded-lg border ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
          <h4 className={`text-sm font-medium mb-2 flex items-center gap-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Web application targets not yet configured for testing
          </h4>
          <div className="space-y-2">
            {orphanTargets.map(target => (
              <div key={target._id} className={`flex items-center justify-between p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div>
                  <span className={`font-medium text-sm ${textPrimary}`}>{target.value}</span>
                  {target.description && (
                    <span className={`ml-2 text-xs ${textMuted}`}>- {target.description}</span>
                  )}
                </div>
                <button
                  onClick={() => handleRecoverOrphan(target)}
                  disabled={recoveringId === target._id}
                  className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:bg-teal-400"
                >
                  {recoveringId === target._id ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    'Setup Web Testing'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {webTargets.length === 0 && orphanTargets.length === 0 && !showForm ? (
        <div className={`text-center py-10 rounded-lg ${cardBg}`}>
          <FontAwesomeIcon icon={faGlobe} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={`text-lg mb-4 ${textSecondary}`}>No web targets configured</p>
          <p className={`mb-6 ${textMuted}`}>Add a web application target to start testing with proxy, spider, intruder, and more.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 flex items-center mx-auto"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add First Web Target
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {webTargets.map(wt => (
            <div
              key={wt._id}
              onClick={() => navigate(`/projects/${projectId}/web-testing/${wt._id}`)}
              className={`rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-teal-600' : 'bg-white border-gray-200 hover:border-teal-400'}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <FontAwesomeIcon icon={faGlobe} className="text-teal-500 text-xl" />
                <div className="flex-1 min-w-0">
                  <h5 className={`font-semibold truncate ${textPrimary}`}>{wt.baseUrl}</h5>
                  <span className={`text-xs ${textMuted}`}>{wt.webType || 'website'}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {wt.siteMap?.length || 0} endpoints
                </span>
                {wt.wafDetected && (
                  <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                    WAF: {wt.wafDetected}
                  </span>
                )}
              </div>
              <div className={`text-xs mt-2 ${textMuted}`}>
                Created: {new Date(wt.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebTestingListPage;
